import React, { Component } from "react";
import styled from "styled-components";

import { Snackbar as MuiSnackbar, Paper, IconButton, Typography, TextField, Checkbox } from "@material-ui/core";
import { ThumbUpOutlined, ThumbDownOutlined, CheckCircle, Close } from "@material-ui/icons";

import { Button } from "js/Components/Button";
import { renderReactDialogFromJSX } from "js/react/renderReactRoot";
import { trackActivity } from "js/core/utilities/utilities";
import { themeColors } from "js/react/sharedStyles";
import PresentationEditorController from "js/editor/PresentationEditor/PresentationEditorController";

const StyledSnackbar = styled(MuiSnackbar)`
    .MuiSnackbarContent-root {
        display: none; // Hide default content, we'll use custom Paper
    }
`;

const SharedContainer = styled(Paper)`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 5px;
    border-radius: 5px;
    border: 2px solid ${themeColors.ui_blue} !important;
    background: #FFF;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    min-height: 55px;
    min-width: 400px; /* Fixed minimum width to ensure consistency */
`;

const QuestionContent = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    padding: 10px 15px;
    gap: 15px;
    width: 100%;
`;

const ConfirmationContent = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    justify-content: center;
`;

const QuestionText = styled(Typography)`
    color: #444 !important;
    font-feature-settings: 'liga' off, 'clig' off !important;
    font-family: "Source Sans Pro" !important;
    font-size: 16px !important;
    font-style: normal !important;
    font-weight: 600 !important;
    line-height: 100% !important; /* 16px */
`;

const ButtonContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 15px;
`;

const FeedbackButton = styled(IconButton)`
    width: 24px;
    height: 24px;
    border-radius: 4px;
    padding: 0;
    
    &:hover {       
        background: white; 
        .MuiSvgIcon-root {
            color: ${themeColors.ui_blue};
        }
    }
    
    .MuiSvgIcon-root {
        font-size: 24px;
        color: ${themeColors.mediumGray};
    }
`;

const ConfirmationIcon = styled(CheckCircle)`
    color: #96BB47;
    font-size: 18px;
`;

const ConfirmationText = styled(Typography)`
    color: #444 !important;
    font-feature-settings: 'liga' off, 'clig' off !important;
    font-family: "Source Sans Pro" !important;
    font-size: 16px !important;
    font-style: normal !important;
    font-weight: 600 !important;
    line-height: 100% !important; /* 16px */
`;

const CloseButton = styled(IconButton)`
    position: absolute !important;
    width: 15px;
    height: 15px;
    top: 5px;
    right: 5px;

    .MuiSvgIcon-root {
        font-size: 15px;
        color: #999 !important;
    }
    
    &:hover {
        background: #f5f5f5;
    }
`;

const FeedbackInputContent = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: stretch;
    padding: 10px 15px 25px 15px;
    gap: 10px;
    width: 100%;
    height: 100%;
`;

const FeedbackTextField = styled(TextField)`
    width: 100%;
    flex: 1;
    
    .MuiInputBase-root {
        font-size: 14px;
        background: white;
        border-radius: 4px;
        height: 100%;
        align-items: flex-start;
    }
    
    .MuiInputBase-input {
        height: 100%;
        padding: 8px;
        font-size: 14px;
        color: #333;
        
        &::placeholder {
            color: #999;
            opacity: 1;
        }
    }
    
    .MuiOutlinedInput-multiline {
        padding: 5px;
    }
    
    .MuiOutlinedInput-notchedOutline {
        border-color: #d1d9e0 !important;
    }
    
    &:hover .MuiOutlinedInput-notchedOutline {
        border-color: #d1d9e0 !important;
    }
    
    &.Mui-focused .MuiOutlinedInput-notchedOutline {
        border-color: #d1d9e0 !important;
        border-width: 1px !important;
    }
    
    .MuiOutlinedInput-root {
        &.Mui-focused .MuiOutlinedInput-notchedOutline {
            border-color: #d1d9e0 !important;
            border-width: 1px !important;
        }
        
        &:hover .MuiOutlinedInput-notchedOutline {
            border-color: #d1d9e0 !important;
        }
    }
`;

const SendButtonContainer = styled.div`
    position: absolute;
    bottom: 10px;
    right: 5px;

    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 10px;
    height: 20px;

    > button {
        font-family: "Source Sans Pro" !important;
        font-size: 14px !important;
        font-style: normal !important;
        font-weight: 600 !important;
        line-height: normal !important;
        letter-spacing: 1px !important;
        text-transform: uppercase !important;
    }
`;

const FeedbackQuestionText = styled(Typography)`
    color: #444 !important;
    font-feature-settings: 'liga' off, 'clig' off !important;
    font-family: "Source Sans Pro" !important;
    font-size: 16px !important;
    font-style: normal !important;
    font-weight: 600 !important;
    line-height: 100% !important; /* 16px */
`;

const FeedbackPromptShareContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 5px;
    height: 20px;
`;

const SharePromptText = styled(Typography)` 
    color: #666 !important;
    font-feature-settings: 'liga' off, 'clig' off !important;
    font-family: "Source Sans Pro" !important;
    font-size: 12px !important;
    font-style: normal !important;
    font-weight: 600 !important;
    line-height: 100% !important; /* 12px */
`;

export class AIFeedbackSurvey extends Component {
    state = {
        step: "question", // 'question', 'feedback', 'confirmation'
        feedbackGiven: null, // 'positive' or 'negative'
        feedbackText: "",
        isHovering: false,
        sharePrompt: true
    };

    autoHideTimer = null;

    componentDidMount() {
        this.startAutoHideTimer();
    }

    componentWillUnmount() {
        if (this.autoHideTimer) {
            clearTimeout(this.autoHideTimer);
        }
    }

    startAutoHideTimer = () => {
        // Auto-dismiss after 10 seconds if no interaction and not hovering
        this.autoHideTimer = setTimeout(() => {
            if (this.state.step === "question" && !this.state.isHovering) {
                this.props.onClose && this.props.onClose();
            } else if (this.state.step === "question" && this.state.isHovering) {
                // Restart timer if still hovering
                this.startAutoHideTimer();
            }
        }, 20000);
    };

    handleMouseEnter = () => {
        this.setState({ isHovering: true });
        // Clear the current timer when hovering starts
        if (this.autoHideTimer) {
            clearTimeout(this.autoHideTimer);
        }
    };

    handleMouseLeave = () => {
        this.setState({ isHovering: false });
        // Restart timer when hover ends (only if still on question step)
        if (this.state.step === "question") {
            this.startAutoHideTimer();
        }
    };

    handleFeedback = type => {
        // Clear the auto-hide timer
        if (this.autoHideTimer) {
            clearTimeout(this.autoHideTimer);
        }

        this.setState({
            feedbackGiven: type
        });

        if (type === "positive") {
            // Skip to confirmation for positive feedback
            this.setState({ step: "confirmation" });

            // Auto-close after showing confirmation for 3 seconds
            setTimeout(() => {
                this.handleClose();
            }, 3000);
        } else {
            // Go to feedback input step for negative feedback
            this.setState({ step: "feedback" });

            // Set flag in editor controller so that we block key events
            PresentationEditorController.setPopupState(true);
        }

        // Track analytics for AI generation quality feedback
        trackActivity("PresentationGeneration", "SatisfactionScore", null, null, {
            feedback_value: type === "positive" ? "thumbs_up" : "thumbs_down"
        });
    };

    handleClose = () => {
        if (this.autoHideTimer) {
            clearTimeout(this.autoHideTimer);
        }
        this.handleClose();
    };

    handleFeedbackTextChange = event => {
        this.setState({ feedbackText: event.target.value });
    };

    handleFeedbackSubmit = () => {
        // Clear any existing timer
        if (this.autoHideTimer) {
            clearTimeout(this.autoHideTimer);
        }

        // Set flag in editor controller so that we block key events
        PresentationEditorController.setPopupState(false);

        // Move to confirmation step
        this.setState({ step: "confirmation" });

        // Auto-close after showing confirmation for 3 seconds
        setTimeout(() => {
            this.handleClose();
        }, 3000);

        // Track analytics for text feedback submission
        trackActivity("PresentationGeneration", "SatisfactionContext", null, null, {
            feedback_context: this.state.feedbackText,
            feedback_share_prompt: this.props.prompt,
            feedback_had_additional_context: !!this.props.hadAdditionalContext
        });
    };

    handleClose = () => {
        // set flag in editor controller so that we unblock key events
        PresentationEditorController.setPopupState(false);

        // close the popup
        this.props.onClose && this.props.onClose();
    };

    renderSurveyContent = () => (
        <QuestionContent>
            <QuestionText>Satisfied with the results?</QuestionText>
            <ButtonContainer>
                <FeedbackButton onClick={() => this.handleFeedback("positive")}>
                    <ThumbUpOutlined />
                </FeedbackButton>
                <FeedbackButton onClick={() => this.handleFeedback("negative")}>
                    <ThumbDownOutlined />
                </FeedbackButton>
            </ButtonContainer>
        </QuestionContent>
    );

    renderFeedbackContent = () => (
        <>
            <FeedbackInputContent>
                <FeedbackQuestionText>What didn't meet your needs?</FeedbackQuestionText>
                <FeedbackTextField
                    variant="outlined"
                    multiline
                    rows={3}
                    placeholder="Your feedback..."
                    value={this.state.feedbackText}
                    onChange={this.handleFeedbackTextChange}
                    onKeyPress={e => {
                        if (e.key === "Enter" && e.ctrlKey && this.state.feedbackText.trim()) {
                            this.handleFeedbackSubmit();
                        }
                    }}
                />
                <FeedbackPromptShareContainer>
                    <Checkbox
                        size="small"
                        color="primary"
                        style={{ padding: 0 }}
                        checked={this.state.sharePrompt}
                        onChange={e => {
                            this.setState({ sharePrompt: e.target.checked });
                        }}
                    />
                    <SharePromptText>Share prompt with Beautiful.ai</SharePromptText>
                </FeedbackPromptShareContainer>

            </FeedbackInputContent>
            <SendButtonContainer>
                <Button unframed blue
                    onClick={this.handleFeedbackSubmit}
                    disabled={this.state.feedbackText.trim() === "" && !this.state.sharePrompt}
                >SEND</Button>
            </SendButtonContainer>
        </>
    );

    renderConfirmationContent = () => (
        <ConfirmationContent>
            <ConfirmationIcon />
            <ConfirmationText>Thank you!</ConfirmationText>
        </ConfirmationContent>
    );

    render() {
        const { step } = this.state;
        const { open, onClose, ...otherProps } = this.props;

        const renderContent = () => {
            switch (step) {
                case "question":
                    return this.renderSurveyContent();
                case "feedback":
                    return this.renderFeedbackContent();
                case "confirmation":
                    return this.renderConfirmationContent();
                default:
                    return this.renderSurveyContent();
            }
        };

        return (
            <StyledSnackbar
                open={open}
                onClose={this.handleClose}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "center"
                }}
                {...otherProps}
            >
                <SharedContainer
                    elevation={0}
                    onMouseEnter={this.handleMouseEnter}
                    onMouseLeave={this.handleMouseLeave}
                >
                    {renderContent()}
                    {step !== "confirmation" && (
                        <CloseButton onClick={this.handleClose}>
                            <Close />
                        </CloseButton>
                    )}
                </SharedContainer>
            </StyledSnackbar>
        );
    }
}

export const ShowAIFeedbackSurvey = (props = {}) => {
    let allowClickaway = false;
    let dialog;

    const snackbarProps = {
        open: true,
        onClose: (event, reason) => {
            if (reason !== "clickaway" || allowClickaway) {
                dialog && dialog.close();
            }
        },
        autoHideDuration: null, // Don't auto-hide, let user interact or manual close
        ...props,
    };

    setTimeout(() => allowClickaway = true, 100);

    dialog = renderReactDialogFromJSX(
        <AIFeedbackSurvey {...snackbarProps} />,
        {
            overlayIsUnblocking: true, // Allows clicks to pass thru the overlay
            unmountOnReroute: false, // Allows the snackbar to persist across reroutes
            registerDialog: false, // Allows key input in the editor while snackbar is visible
        },
    );

    return dialog;
};
